import {
    IClothItem,
    IShopPhoto
} from "./../../../../../types/index";
import s from './photoBlock.module.css';
import React, {useEffect, useState, useRef, useCallback} from "react";
import {toast} from "react-toastify";
import {appAxiosInstance} from "../../../../../api";
import {Button, Modal} from "react-bootstrap";
import {ReactImageGalleryItem} from "react-image-gallery";
import {mirrorImage} from './mirrorPhoto';

interface IPhotoBlockProps {
    cloth: IClothItem | undefined;
    onClothUpdate?: () => void;
    isNew?: boolean;
    onAddAction?: (action: (clothId: string) => Promise<void>) => Promise<void>;
    hasRequiredFields?: () => boolean;
    createdClothId?: string | null;
}

interface ICustomImageGalleryItem extends ReactImageGalleryItem {
    isShopPhoto: boolean;
    isCollagePhoto: boolean;
    shopIndex: number;
    photoId?: string;
    isApproved: boolean;
}

export function PhotoBlock({cloth, onClothUpdate, isNew, onAddAction, hasRequiredFields, createdClothId}: IPhotoBlockProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isPhotosLoading, setIsPhotosLoading] = useState(false);
    const [images, setImages] = useState<Array<ICustomImageGalleryItem>>([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isImageMenuOpen, setIsImageMenuOpen] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>('');
    const [zoomLevel, setZoomLevel] = useState(1);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({x: 0, y: 0});
    const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const [requiredDeletePhotoWindow, setRequiredDeletePhotoWindow] = useState(false);
    const [shopPhotoIds, setShopPhotoIds] = useState<IShopPhoto[]>([]);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [localCreatedClothId, setLocalCreatedClothId] = useState<string | null>(null);
    const [uploadInProgress, setUploadInProgress] = useState(false);

    useEffect(() => {
        if (createdClothId) {
            setLocalCreatedClothId(createdClothId);
        }
    }, [createdClothId]);

    useEffect(() => {
        if (cloth?.id) {
            setLocalCreatedClothId(cloth.id.toString());
        }
    }, [cloth]);

    const updateGallery = useCallback(() => {
        setIsPhotosLoading(true);

        const bufImages = cloth?.photoLinks?.map(item => {
            const photoItem: ICustomImageGalleryItem = {
                original: item.original || '',
                thumbnail: item.thumbnail || item.original || '',
                description: item.photoId || '',
                isShopPhoto: item.isShopPhoto || false,
                isCollagePhoto: item.isCollagePhoto || false,
                shopIndex: item.shopIndex || 0,
                photoId: item.photoId,
                isApproved: item.isApproved,
            };
            return photoItem;
        }) || [];

        const sortedImages = sortImages(bufImages);

        const shopPhotos = cloth?.photoLinks
            ?.filter(photo => photo.isShopPhoto)
            ?.sort((a, b) => (a.shopIndex || 0) - (b.shopIndex || 0))
            ?.map((photo, index) => ({
                index: photo.shopIndex || index + 1,
                link: photo.original
            })) || [];

        setImages(sortedImages);
        setShopPhotoIds(shopPhotos);

        const collageIndex = sortedImages.findIndex(img => img.isCollagePhoto);
        if (collageIndex !== -1) {
            setSelectedImageIndex(collageIndex);
        }

        setIsPhotosLoading(false);
    }, [cloth]);

    const savePhoto = async (photo: any, clothId?: string) => {
        try {
            const currentClothId = clothId || localCreatedClothId || cloth?.id?.toString();
            if (currentClothId && currentClothId !== "-1") {
                await appAxiosInstance.post(
                    `v2/clothes/photo/save/${currentClothId}`,
                    { objectStorageLink: photo.objectStorageLink }
                );
            } else {
                toast.error("Не удалось сохранить фото: ID одежды не определен");
            }
        } catch (error) {
            toast.error("Ошибка сохранения фото");
        }
    };

    const uploadPhoto = async (newImages: ICustomImageGalleryItem[], specificClothId?: string) => {
        try {
            setUploadInProgress(true);
            const currentClothId = specificClothId || localCreatedClothId || cloth?.id?.toString();

            if (!currentClothId || currentClothId === "-1") {
                toast.error("Не удалось загрузить фото: ID одежды не определен");
                return;
            }

            const uploadPromises = newImages.map(async (image, i) => {
                const formData = new FormData();

                const dataUrl = image.original;
                const base64Data = dataUrl.split(',')[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);

                for (let j = 0; j < byteCharacters.length; j++) {
                    byteNumbers[j] = byteCharacters.charCodeAt(j);
                }

                const byteArray = new Uint8Array(byteNumbers);
                const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
                const blob = new Blob([byteArray], {type: mimeString});

                const timestamp = Date.now();
                const fileName = `image_${timestamp}_${i}.${mimeString.split('/')[1] || 'jpg'}`;

                formData.append('photo', blob, fileName);
                formData.append('clothId', currentClothId);

                if (cloth?.colorCloth != null){
                    formData.append('code', cloth.colorCloth);
                } else {
                    formData.append('code', "2433");
                }

                const authToken = localStorage.getItem('authToken');

                const response = await appAxiosInstance.post('/backend/photo/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'AuthToken': authToken,
                    }
                });

                if (response.data.fileName != null) {
                    const object = {
                        objectStorageLink: response.data.fileName
                    };
                    await savePhoto(object, currentClothId);
                    return true;
                } else {
                    toast.error(`Ошибка при загрузке фото ${i + 1}: Неизвестная ошибка`);
                    return false;
                }
            });

            await Promise.all(uploadPromises);
            toast.success(`Все фото успешно загружены`);

            if (onClothUpdate) {
                await onClothUpdate();
            }

        } catch (error: any) {
            toast.error(`Ошибка при загрузке фото: ${error?.message || 'Неизвестная ошибка'}`);
        } finally {
            setUploadInProgress(false);
        }
    };

    const readFileAsDataURL = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target && e.target.result) {
                    resolve(e.target.result.toString());
                } else {
                    reject(new Error('Ошибка чтения файла'));
                }
            };
            reader.onerror = () => {
                reject(new Error('Ошибка чтения файла'));
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const uploadPhotosAction = async (currentClothId: string) => {
            const files = Array.from(event.target.files!);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            const newImages: ICustomImageGalleryItem[] = [];

            try {
                const readPromises = files.map(async (file) => {
                    try {
                        const imageData = await readFileAsDataURL(file);
                        const newImage: ICustomImageGalleryItem = {
                            original: imageData,
                            thumbnail: imageData,
                            description: `temp-${Date.now()}-${Math.random()}`,
                            isShopPhoto: false,
                            isCollagePhoto: false,
                            shopIndex: 0,
                            isApproved: true,
                        };
                        return newImage;
                    } catch (error) {
                        toast.error(`Ошибка чтения файла: ${file.name}`);
                        return null;
                    }
                });

                const loadedImages = (await Promise.all(readPromises)).filter((img): img is ICustomImageGalleryItem => img !== null);

                if (loadedImages.length > 0) {
                    await uploadPhoto(loadedImages, currentClothId);
                }
            } catch (error) {
                toast.error('Ошибка при обработке файлов');
            }
        };

        if (isNew && onAddAction) {
            onAddAction(uploadPhotosAction);
        } else {
            const currentClothId = localCreatedClothId || cloth?.id?.toString();
            if (currentClothId && currentClothId !== "-1") {
                uploadPhotosAction(currentClothId);
            } else {
                toast.error("ID одежды не найден");
            }
        }
    };

    const sortImages = (images: ICustomImageGalleryItem[]): ICustomImageGalleryItem[] => {
        return [...images].sort((a, b) => {
            const aIsCollage = a.isCollagePhoto;
            const bIsCollage = b.isCollagePhoto;

            if (aIsCollage && !bIsCollage) return -1;
            if (!aIsCollage && bIsCollage) return 1;

            return 0;
        });
    };

    const openImageModal = (imageUrl: string) => {
        setSelectedImage(imageUrl);
        setIsImageModalOpen(true);
        resetZoom();
        setIsImageLoaded(false);
    };

    const showModalDeletePhotoAgree = () => {
        setRequiredDeletePhotoWindow(true);
    };

    const resetZoom = () => {
        setZoomLevel(1);
        setPosition({x: 0, y: 0});
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            const newX = e.clientX - startPos.x;
            const newY = e.clientY - startPos.y;

            if (imageRef.current) {
                const rect = imageRef.current.getBoundingClientRect();

                if (zoomLevel > 1) {
                    const constrained = constrainPosition(newX, newY, zoomLevel, rect);
                    setPosition(constrained);
                } else {
                    setPosition({x: newX, y: newY});
                }
            }
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            setIsDragging(true);
            setStartPos({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y
            });
        } else if (e.touches.length === 2) {
            setIsDragging(false);
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(
                touch1.clientX - touch2.clientX,
                touch1.clientY - touch2.clientY
            );
            setLastTouchDistance(distance);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 1 && isDragging) {
            const newX = e.touches[0].clientX - startPos.x;
            const newY = e.touches[0].clientY - startPos.y;

            if (imageRef.current) {
                const rect = imageRef.current.getBoundingClientRect();

                if (zoomLevel > 1) {
                    const constrained = constrainPosition(newX, newY, zoomLevel, rect);
                    setPosition(constrained);
                } else {
                    setPosition({x: newX, y: newY});
                }
            }
        } else if (e.touches.length === 2 && lastTouchDistance !== null) {
            e.preventDefault();

            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(
                touch1.clientX - touch2.clientX,
                touch1.clientY - touch2.clientY
            );

            const scale = distance / lastTouchDistance;
            let newZoom = zoomLevel * scale;

            if (newZoom < 1.0) {
                newZoom = 1.0;
            }
            newZoom = Math.min(newZoom, 5);

            if (imageRef.current) {
                const rect = imageRef.current.getBoundingClientRect();

                const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
                const centerY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

                const zoomFactor = newZoom / zoomLevel;

                const newX = centerX - (centerX - position.x) * zoomFactor;
                const newY = centerY - (centerY - position.y) * zoomFactor;

                const constrained = constrainPosition(newX, newY, newZoom, rect);

                setPosition(constrained);
                setZoomLevel(newZoom);
            }

            setLastTouchDistance(distance);
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (e.touches.length === 0) {
            setIsDragging(false);
            setLastTouchDistance(null);
        } else if (e.touches.length === 1) {
            setLastTouchDistance(null);

            const touch = e.touches[0];
            setStartPos({
                x: touch.clientX - position.x,
                y: touch.clientY - position.y
            });
        }
    };

    const zoomIn = () => {
        if (!imageRef.current) return;

        const newZoom = Math.min(zoomLevel * 1.25, 5);
        const rect = imageRef.current.getBoundingClientRect();

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const zoomFactor = newZoom / zoomLevel;

        const newX = centerX - (centerX - position.x) * zoomFactor;
        const newY = centerY - (centerY - position.y) * zoomFactor;

        const constrained = constrainPosition(newX, newY, newZoom, rect);

        setPosition(constrained);
        setZoomLevel(newZoom);
    };

    const zoomOut = () => {
        if (!imageRef.current) return;

        const newZoom = Math.max(zoomLevel / 1.5, 1.0);

        if (newZoom === 1.0) {
            setPosition({x: 0, y: 0});
            setZoomLevel(newZoom);
            return;
        }

        const rect = imageRef.current.getBoundingClientRect();

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const zoomFactor = newZoom / zoomLevel;

        const newX = centerX - (centerX - position.x) * zoomFactor;
        const newY = centerY - (centerY - position.y) * zoomFactor;

        const constrained = constrainPosition(newX, newY, newZoom, rect);

        setPosition(constrained);
        setZoomLevel(newZoom);
    };

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();

        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();
        const delta = -Math.sign(e.deltaY);
        const scale = delta > 0 ? 1.2 : 0.8;

        let newZoom = zoomLevel * scale;

        if (newZoom < 1.0) {
            newZoom = 1.0;
            setPosition({x: 0, y: 0});
            setZoomLevel(newZoom);
            return;
        }

        newZoom = Math.min(newZoom, 5);

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const zoomFactor = newZoom / zoomLevel;

        const newX = mouseX - (mouseX - position.x) * zoomFactor;
        const newY = mouseY - (mouseY - position.y) * zoomFactor;

        const constrained = constrainPosition(newX, newY, newZoom, rect);

        setPosition(constrained);
        setZoomLevel(newZoom);
    };

    const constrainPosition = (x: number, y: number, currentZoom: number, container: DOMRect) => {
        if (currentZoom <= 1) {
            const maxMove = 50;
            return {
                x: Math.max(-maxMove, Math.min(maxMove, x)),
                y: Math.max(-maxMove, Math.min(maxMove, y))
            };
        }

        const scaledWidth = container.width * currentZoom;
        const scaledHeight = container.height * currentZoom;

        const maxX = Math.max(0, (scaledWidth - container.width));
        const maxY = Math.max(0, (scaledHeight - container.height) * 4);

        return {
            x: Math.max(-maxX, Math.min(maxX, x)),
            y: Math.max(-maxY, Math.min(maxY, y))
        };
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setStartPos({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setSelectedImage('');
        resetZoom();
        setIsImageLoaded(false);
    };

    const navigateImage = (direction: 'next' | 'prev') => {
        const newIndex = direction === 'next'
            ? (selectedImageIndex + 1) % images.length
            : (selectedImageIndex - 1 + images.length) % images.length;

        setSelectedImageIndex(newIndex);
        setSelectedImage(images[newIndex]?.original || '');
        resetZoom();
        setIsImageLoaded(false);
    };

    const handleDeletePhoto = async () => {
        if (images.length === 0) return;

        const imageToDelete = images[selectedImageIndex];
        const photoId = imageToDelete.photoId;

        if (!photoId) {
            toast.error('Не удалось определить ID фото для удаления');
            return;
        }

        try {
            const response = await appAxiosInstance.post(`v2/clothes/photo/delete/${photoId}`);

            if (response.status === 200) {
                const updatedImages = images.filter((_, index) => index !== selectedImageIndex);
                setImages(updatedImages);

                if (updatedImages.length > 0) {
                    setSelectedImageIndex(Math.min(selectedImageIndex, updatedImages.length - 1));
                }

                toast.success('Фото удалено');

                if (onClothUpdate) {
                    onClothUpdate();
                }
            } else {
                toast.error('Ошибка при удалении фото');
            }
        } catch (error) {
            toast.error('Ошибка при удалении фото');
        } finally {
            setRequiredDeletePhotoWindow(false);
            setIsImageMenuOpen(false);
        }
    };

    const setCollagePhoto = async () => {
        const currentImage = images[selectedImageIndex];

        if (!currentImage || !currentImage.photoId || !cloth) {
            toast.error('Не удалось установить коллажное фото');
            return;
        }

        try {
            await appAxiosInstance.post(`v2/clothes/photo/collagePhoto/${cloth.id}/${currentImage.photoId}`);

            const updatedImages = images.map((img, index) => ({
                ...img,
                isCollagePhoto: index === selectedImageIndex
            }));

            const sortedImages = sortImages(updatedImages);
            setImages(sortedImages);

            toast.success('Коллажное фото установлено');

            if (onClothUpdate) {
                onClothUpdate();
            }
        } catch (error) {
            toast.error('Ошибка при установке коллажного фото');
        } finally {
            setIsImageMenuOpen(false);
        }
    };

    const setShopPhoto = async () => {
        const currentImage = images[selectedImageIndex];

        if (!currentImage || !currentImage.photoId) {
            toast.error('Не удалось обновить статус фото для магазина');
            return;
        }

        const newIsShopPhoto = !currentImage.isShopPhoto;
        const currentShopIndex = currentImage.isShopPhoto ? 0 : (shopPhotoIds.length + 1);

        try {
            const response = await appAxiosInstance.post('v2/clothes/photo/editPhotoShop', {
                photoId: Number(currentImage.photoId),
                isChecked: newIsShopPhoto,
                shopIndex: currentShopIndex
            });

            if (response.data) {
                const updatedImages = images.map((img, index) =>
                    index === selectedImageIndex
                        ? { ...img, isShopPhoto: newIsShopPhoto, shopIndex: currentShopIndex }
                        : img
                );

                setImages(updatedImages);

                if (newIsShopPhoto) {
                    const newShopPhoto: IShopPhoto = {
                        index: currentShopIndex,
                        link: currentImage.original
                    };
                    setShopPhotoIds(prev => [...prev, newShopPhoto]);
                } else {
                    setShopPhotoIds(prev => prev.filter(photo => photo.link !== currentImage.original));
                }

                toast.success(newIsShopPhoto
                    ? `Фото назначено для магазина (№${currentShopIndex})`
                    : 'Фото убрано из магазина'
                );

                if (onClothUpdate) {
                    onClothUpdate();
                }
            } else {
                toast.error('Ошибка сервера при обновлении статуса фото');
            }
        } catch (error) {
            toast.error('Ошибка при обновлении статуса фото');
        } finally {
            setIsImageMenuOpen(false);
        }
    };

    const closeDeletePhotoWindow = () => {
        setRequiredDeletePhotoWindow(false);
    };

    const mirrorPhoto = async () => {
        const currentImage = images[selectedImageIndex];

        setIsImageMenuOpen(false);

        if (!currentImage || !currentImage.original) {
            toast.error('Не удалось отразить фото: изображение не найдено');
            return;
        }

        try {
            const mirroredDataUrl = await mirrorImage(currentImage.original);

            const newImage: ICustomImageGalleryItem = {
                original: mirroredDataUrl,
                thumbnail: mirroredDataUrl,
                description: `mirrored-${Date.now()}`,
                isShopPhoto: false,
                isCollagePhoto: false,
                shopIndex: 0,
                isApproved: true,
            };

            await uploadPhoto([newImage]);
        } catch (error) {
            toast.error('Ошибка при отражении фото');
        }
    };

    const handleImageLoad = () => {
        setIsImageLoaded(true);
    };

    useEffect(() => {
        if (cloth) {
            updateGallery();
        }
    }, [cloth, updateGallery]);

    useEffect(() => {
        if (isImageModalOpen) {
            document.body.classList.add(s.noScroll);
            document.documentElement.classList.add(s.noScroll);
        } else {
            document.body.classList.remove(s.noScroll);
            document.documentElement.classList.remove(s.noScroll);
        }

        return () => {
            document.body.classList.remove(s.noScroll);
            document.documentElement.classList.remove(s.noScroll);
        };
    }, [isImageModalOpen]);

    const isAddButtonDisabled = (isNew && hasRequiredFields && !hasRequiredFields()) || uploadInProgress;

    return (
        <>
            <div className={s.modernImageSection}>
                <div className={s.modernSectionHeader}>
                    <h3>Фотографии модели</h3>
                    <button
                        className={s.modernBtnPrimary}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isPhotosLoading || isAddButtonDisabled}
                        title={isAddButtonDisabled ?
                            "Заполните обязательные поля для добавления фото" : ""}
                    >
                        {uploadInProgress ? 'Загрузка...' : (isPhotosLoading ? 'Загрузка...' : '+ Добавить фото')}
                    </button>
                </div>

                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{display: 'none'}}
                    multiple
                    accept="image/*"
                />

                {isPhotosLoading || uploadInProgress ? (
                    <div className={s.modernNoImages}>
                        {uploadInProgress ? 'Загрузка новых фото...' : 'Загрузка фотографий...'}
                    </div>
                ) : images.length > 0 ? (
                    <div className={s.modernImageGallery}>
                        <div className={s.modernMainImageContainer}>
                            <div className={s.modernImageWrapper}>
                                <img
                                    src={images[selectedImageIndex]?.original}
                                    alt={`Изображение ${selectedImageIndex + 1}`}
                                    onClick={() => openImageModal(images[selectedImageIndex]?.original)}
                                    className={s.modernMainImage}
                                />
                            </div>

                            <div className={s.modernImageActions}>
                                <button
                                    className={s.modernActionBtn}
                                    onClick={() => setIsImageMenuOpen(!isImageMenuOpen)}
                                    disabled={uploadInProgress}
                                >
                                    ⋮
                                </button>

                                {isImageMenuOpen && (
                                    <div className={s.modernActionMenu}>
                                        <button onClick={setCollagePhoto}>
                                            Назначить коллажным фото
                                        </button>
                                        <button onClick={setShopPhoto}>
                                            {images[selectedImageIndex]?.isShopPhoto
                                                ? "Убрать фото товара"
                                                : "Использовать как фото товара"}
                                        </button>
                                        <button onClick={mirrorPhoto}>
                                            Отразить
                                        </button>
                                        <button onClick={showModalDeletePhotoAgree}>
                                            Удалить фото
                                        </button>
                                    </div>
                                )}
                            </div>

                            {images[selectedImageIndex]?.isShopPhoto && (
                                <div className={s.modernShopBadge}>
                                    {images[selectedImageIndex]?.shopIndex || ''}
                                </div>
                            )}

                            {images[selectedImageIndex]?.isCollagePhoto && (
                                <div className={`${s.modernShopBadge} ${s.collage}`}>
                                    K
                                </div>
                            )}
                        </div>

                        <div className={s.modernThumbnailStrip}>
                            {images.map((image, index) => (
                                <div
                                    key={index}
                                    className={`${s.modernThumbnail} ${index === selectedImageIndex ? s.active : ''}`}
                                    onClick={() => {
                                        setSelectedImageIndex(index);
                                        setIsImageMenuOpen(false);
                                    }}
                                >
                                    <img
                                        src={image.thumbnail || image.original}
                                        alt={`Миниатюра ${index + 1}`}
                                        className={`${s.modernThumbnailImage} ${!image?.isApproved ? s.isNotApproved : ''} `}
                                    />

                                    {image.isShopPhoto && (
                                        <div className={s.modernThumbnailBadge}>
                                            {image.shopIndex}
                                        </div>
                                    )}

                                    {image.isCollagePhoto && (
                                        <div className={`${s.modernThumbnailBadge} ${s.collage}`}>
                                            K
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className={s.modernNoImages}>
                        Нет изображений товара
                    </div>
                )}
            </div>

            <Modal show={requiredDeletePhotoWindow} onHide={closeDeletePhotoWindow} className={s.modernDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Удалить фото?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Вы уверены, что хотите удалить выбранное фото?
                    {images[selectedImageIndex] && (
                        <div style={{marginTop: '10px', textAlign: 'center'}}>
                            <img
                                src={images[selectedImageIndex].thumbnail}
                                alt="Предпросмотр"
                                style={{maxWidth: '150px', maxHeight: '150px', objectFit: 'contain'}}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDeletePhotoWindow}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDeletePhoto}>
                        Удалить
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={isImageModalOpen}
                onHide={closeImageModal}
                size="xl"
                centered
                className={s.imageModal}
                contentClassName={s.transparentModal}
            >
                <Modal.Body className={s.imageNavigation} onClick={closeImageModal}>
                    <button
                        className={s.navButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('prev');
                        }}
                    >
                        ‹
                    </button>

                    <div
                        ref={imageRef}
                        className={s.imageWrapper}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }}
                    >
                        <div
                            className={s.zoomContainer}
                            style={{
                                transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                                transformOrigin: '0 0',
                                transition: isDragging ? 'none' : 'transform 0.2s ease'
                            }}
                        >
                            <img
                                src={selectedImage}
                                alt="Увеличенное изображение"
                                className={s.fullscreenImage}
                                onLoad={handleImageLoad}
                                style={{
                                    cursor: isDragging ? 'grabbing' : 'grab'
                                }}
                            />
                        </div>
                    </div>

                    <button
                        className={s.navButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('next');
                        }}
                    >
                        ›
                    </button>

                    <div className={s.zoomControls}>
                        <button
                            className={s.zoomButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                zoomOut();
                            }}
                            title="Уменьшить"
                            disabled={zoomLevel <= 1.0}
                        >
                            −
                        </button>

                        <button
                            className={s.zoomButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                resetZoom();
                            }}
                            title="Сбросить масштаб"
                            disabled={zoomLevel === 1.0 && position.x === 0 && position.y === 0}
                        >
                            1:1
                        </button>

                        <button
                            className={s.zoomButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                zoomIn();
                            }}
                            title="Увеличить"
                            disabled={zoomLevel >= 5}
                        >
                            +
                        </button>

                        <span className={s.zoomLevel}>
                            {(zoomLevel * 100).toFixed(0)}%
                        </span>
                    </div>

                    <button
                        className={s.transparentCloseButton}
                        onClick={closeImageModal}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </Modal.Body>
            </Modal>
        </>
    );
}